import {
  BooleanInput,
  Edit,
  FormDataConsumer,
  ListButton,
  RadioButtonGroupInput,
  SimpleForm,
  TextInput,
  TopToolbar,
  regex,
  required,
  useRecordContext,
} from 'react-admin';
import { Alert, Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import type { Template } from '../../types';

const validateRepoUrl = regex(/^(https?:\/\/\S+|git@\S+:\S+)?$/, 'Ожидается https://… или git@host:org/repo.git');

const SchemaTable = () => {
  const record = useRecordContext<Template>();
  const fields = record?.settingsSchema?.fields || [];
  if (fields.length === 0) return null;
  return (
    <Box sx={{ mt: 2, width: '100%' }}>
      <Typography variant="subtitle1" gutterBottom>
        Настройки клиента для этого шаблона
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Набор полей задаётся миграцией вместе с кодом сборки шаблона и здесь не редактируется.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Ключ</TableCell>
            <TableCell>Название</TableCell>
            <TableCell>Тип</TableCell>
            <TableCell>Обязательное</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((f) => (
            <TableRow key={f.key}>
              <TableCell>
                <code>{f.key}</code>
              </TableCell>
              <TableCell>{f.label}</TableCell>
              <TableCell>
                {f.type}
                {f.platform && <Chip label={f.platform} size="small" variant="outlined" sx={{ ml: 1 }} />}
              </TableCell>
              <TableCell>
                {f.required ? 'да' : f.required_if ? `если ${f.required_if.field} = ${f.required_if.in.join('/')}` : 'нет'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const EditActions = () => (
  <TopToolbar>
    <ListButton />
  </TopToolbar>
);

export const TemplateEdit = () => (
  <Edit mutationMode="pessimistic" actions={<EditActions />}>
    <SimpleForm>
      <TextInput source="key" label="Ключ" disabled />
      <TextInput source="name" label="Название" validate={required()} fullWidth />
      <TextInput
        source="repoUrl"
        label="Репозиторий"
        validate={validateRepoUrl}
        fullWidth
        helperText="Откуда воркер клонирует шаблон. Пока не задан, клиенты на этом шаблоне не собираются"
      />
      <RadioButtonGroupInput
        source="sourceMode"
        label="Из чего собирать"
        choices={[
          { id: 'latest_tag', name: 'Последний релизный тег' },
          { id: 'branch', name: 'Голова ветки' },
        ]}
        validate={required()}
      />
      <FormDataConsumer>
        {({ formData }) => (
          <TextInput
            source="sourceRef"
            label={formData.sourceMode === 'latest_tag' ? 'Шаблон тега' : 'Ветка'}
            helperText={formData.sourceMode === 'latest_tag' ? 'Например v* — берётся старшая версия' : 'Например main'}
            validate={required()}
          />
        )}
      </FormDataConsumer>
      <BooleanInput
        source="active"
        label="Активен"
        helperText="Неактивный шаблон нельзя назначить новым клиентам; клиенты на нём продолжают собираться"
      />
      <Alert severity="info" sx={{ width: '100%' }}>
        Версия шаблона (платформы) берётся из его исходников при каждой сборке. Версия приложения у каждого клиента своя.
      </Alert>
      <SchemaTable />
    </SimpleForm>
  </Edit>
);
